import {noop} from '@benchling/js-utils';
import assert from 'assert';
import {Spec} from 'immutability-helper';
import React, {useCallback, useMemo} from 'react';

import {assertAndContinue} from 'Asserts';
import {makeReactContext} from 'lib/makeReactContext';
import {MetadataForRestore} from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import {InitialCellValue} from 'notebook/structured-tables/util/getInitialCellValueFromLiveValue';
import excess from 'notebook/tables/excess';
import {OutputTableMap} from 'notebook/tables/excess/Evaluator';
import {ReorderOptions} from 'notebook/tables/excess/ops/TableOps';
import * as SpreadsheetEvalUtil from 'notebook/tables/spreadsheets/SpreadsheetEvalUtil';
import {
  InsertableColumnInfo,
  MultiSelectionBox,
  Table,
  TableIndex,
  TableOperation,
  TableOpFunction,
  TableOpOptions,
} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {Note} from 'notebook/util/NoteUtilConstants';

export interface TableOpContextInterface {
  performTableOp: ({index, op, options}: TableOperation) => void;
  deleteTableRowsNonContiguous: (index: number, ranges: Array<[number, number]>) => void;
  deleteTableRows: (index: number, start: number, end: number) => void;
  deleteTableColumns: (index: number, start: number, end: number) => void;
  insertTableRowsAbove: (index: number, start: number, end: number) => void;
  insertTableRowsBelow: (index: number, start: number, end: number) => void;
  reorderTableRowsOrColumns: (index: number, options: ReorderOptions) => void;
  insertTableColumnsLeft: (
    index: number,
    start: number,
    end: number,
    columnInfo: InsertableColumnInfo | {},
    initialCellValues?: Array<InitialCellValue>
  ) => void;
  insertTableColumnsRight: (
    index: number,
    start: number,
    end: number,
    columnInfo: InsertableColumnInfo | {},
    initialCellValues?: Array<InitialCellValue>
  ) => void;
  renameTable: (index: number, newTableName: string) => void;
  mergeTableRegion: (index: number, selectedBox: MultiSelectionBox) => void;
  unmergeTableRegion: (index: number, selectedBox: MultiSelectionBox) => void;
  mergeIntoSingleInput: (index: number, startIndex: number, endIndex: number) => void;
  unmergeSingleInput: (index: number, startIndex: number, endIndex: number) => void;
}

export const TableOpContext = makeReactContext<TableOpContextInterface>(() => {
  assert(false, 'TableOpContext must be used within a TableOpContextProvider');
}, 'NoteContext');

/**
 * This is intended to provide all table operations when used within a withSpreadsheetEvaluation wrapper.
 */
export function TableOpContextProvider<TRoot, TItem>({
  spreadsheetTypes,
  fileId,
  getComputedTables,
  getItems,
  getRoot,
  getType,
  getCid,
  getContent,
  setContent,
  requestSelection,
  updateRoot,
  itemsAccessor,
  children,
}: {
  spreadsheetTypes: Array<string>;
  fileId: string | null;
  getComputedTables: () => OutputTableMap;
  getItems: (root: TRoot) => Array<TItem>;
  getRoot: () => TRoot;
  getType: (item: TItem) => string;
  getCid: (item: TItem) => string;
  getContent: (item: TItem) => {table: Table};
  setContent: (type: string, content: object) => Spec<TItem>;
  requestSelection: (selection: {selection: BenchlingSelection} | MetadataForRestore<TableIndex>) => void;
  updateRoot: (
    root: TRoot,
    opts?: {
      performTableEvaluation?: boolean;
      updatedItemPartIndexes?: Array<number>;
      canIncrementalSave?: boolean;
    },
    updatedRelatedDocuments?: {[documentId: string]: Note}
  ) => void;
  itemsAccessor: (op: {[i: number]: Spec<TItem>}) => Spec<TRoot>;
  children: React.ReactNode;
}): JSX.Element {
  const performTableOp = useCallback(
    ({index, op, options}: TableOperation) => {
      SpreadsheetEvalUtil.performTableOp({
        index,
        fileId,
        op,
        options,
        spreadsheetTypes,
        computedTables: getComputedTables(),
        getItems,
        getRoot,
        getType,
        getCid,
        getContent,
        setContent,
        requestSelection,
        updateRoot,
        itemsAccessor,
      });
    },
    [
      spreadsheetTypes,
      fileId,
      getComputedTables,
      getItems,
      getRoot,
      getType,
      getCid,
      getContent,
      setContent,
      requestSelection,
      updateRoot,
      itemsAccessor,
    ]
  );

  const deleteTableRowsNonContiguous = useCallback(
    (index: number, ranges: Array<[number, number]>): void => {
      if (assertAndContinue(ranges.length > 0, 'Cannot delete rows with empty ranges')) {
        performTableOp({
          index,
          op: (table, documentName) => excess.ops.deleteRowsNonContiguous(table, documentName, ranges),
          options: {forRowRemoval: true, remakeSingleInputs: true},
        });
      }
    },
    [performTableOp]
  );

  const deleteTableRows = useCallback(
    (index: number, start: number, end: number): void => {
      if (end == null) {
        end = start;
      }
      performTableOp({
        index,
        op: (table, documentName) => excess.ops.deleteRows(table, documentName, start, end),
        options: {forRowRemoval: true, remakeSingleInputs: true},
      });
    },
    [performTableOp]
  );

  const deleteTableColumns = useCallback(
    (index: number, start: number, end: number): void => {
      if (end == null) {
        end = start;
      }
      performTableOp({
        index,
        op: (table, documentName) => excess.ops.deleteColumns(table, documentName, start, end),
        options: {forColumnEdit: true},
      });
    },
    [performTableOp]
  );

  const insertTableRowsAbove = useCallback(
    (index: number, start: number, end: number): void => {
      if (end == null) {
        end = start;
      }
      performTableOp({
        index,
        op: (table, documentName) => excess.ops.insertRowsAbove(table, documentName, start, end),
        options: {remakeSingleInputs: true},
      });
    },
    [performTableOp]
  );

  const insertTableRowsBelow = useCallback(
    (index: number, start: number, end: number): void => {
      if (end == null) {
        end = start;
      }
      performTableOp({
        index,
        op: (table, documentName) => excess.ops.insertRowsBelow(table, documentName, start, end),
        options: {remakeSingleInputs: true},
      });
    },
    [performTableOp]
  );

  const reorderTableRowsOrColumns = useCallback(
    (index: number, options: ReorderOptions): void => {
      performTableOp({
        index,
        op: (table, documentName) => excess.ops.reorderRowsOrColumns(table, documentName, options),
        options: {remakeSingleInputs: true},
      });
    },
    [performTableOp]
  );

  const insertTableColumnsLeft = useCallback(
    (
      index: number,
      start: number,
      end: number,
      columnInfo: InsertableColumnInfo | {} = {},
      initialCellValues?: Array<InitialCellValue>
    ): void => {
      if (end == null) {
        end = start;
      }
      performTableOp({
        index,
        op: (table, documentName) =>
          excess.ops.insertColumnsLeft(table, documentName, start, end, columnInfo, initialCellValues),
        options: {forColumnEdit: true, remakeSingleInputs: true},
      });
    },
    [performTableOp]
  );

  const insertTableColumnsRight = useCallback(
    (
      index: number,
      start: number,
      end: number,
      columnInfo: InsertableColumnInfo | {} = {},
      initialCellValues?: Array<InitialCellValue>
    ): void => {
      if (end == null) {
        end = start;
      }
      performTableOp({
        index,
        op: (table, documentName) =>
          excess.ops.insertColumnsRight(table, documentName, start, end, columnInfo, initialCellValues),
        options: {forColumnEdit: true, remakeSingleInputs: true},
      });
    },
    [performTableOp]
  );

  const renameTable = useCallback(
    (index: number, newTableName: string): void => {
      performTableOp({
        index,
        op: (table, documentName, evaluatedTable, tableMap) =>
          excess.ops.renameTable(tableMap, documentName, table.name, newTableName),
      });
    },
    [performTableOp]
  );

  const mergeTableRegion = useCallback(
    (index: number, selectedBox: MultiSelectionBox): void => {
      performTableOp({
        index,
        op: (table) => excess.ops.mergeRegion(table, selectedBox),
      });
    },
    [performTableOp]
  );

  const unmergeTableRegion = useCallback(
    (index: number, selectedBox: MultiSelectionBox): void => {
      performTableOp({
        index,
        op: (table, documentName, evaluatedTable) =>
          excess.ops.unmergeRegion(table, evaluatedTable, selectedBox),
      });
    },
    [performTableOp]
  );

  const mergeIntoSingleInput = useCallback(
    (index: number, startIndex: number, endIndex: number): void => {
      performTableOp({
        index,
        op: (table, documentName, evaluatedTable) =>
          excess.ops.mergeIntoSingleInput(table, documentName, evaluatedTable, startIndex, endIndex),
      });
    },
    [performTableOp]
  );

  const unmergeSingleInput = useCallback(
    (index: number, startIndex: number, endIndex: number): void => {
      performTableOp({
        index,
        op: (table, documentName, evaluatedTable) =>
          excess.ops.unmergeSingleInput(table, documentName, evaluatedTable, startIndex, endIndex),
        options: {remakeSingleInputs: false},
      });
    },
    [performTableOp]
  );

  const contextValue: TableOpContextInterface = useMemo(
    () => ({
      performTableOp,
      deleteTableRowsNonContiguous,
      deleteTableRows,
      deleteTableColumns,
      insertTableRowsAbove,
      insertTableRowsBelow,
      reorderTableRowsOrColumns,
      insertTableColumnsLeft,
      insertTableColumnsRight,
      renameTable,
      mergeTableRegion,
      unmergeTableRegion,
      mergeIntoSingleInput,
      unmergeSingleInput,
    }),
    [
      performTableOp,
      deleteTableRowsNonContiguous,
      deleteTableRows,
      deleteTableColumns,
      insertTableRowsAbove,
      insertTableRowsBelow,
      reorderTableRowsOrColumns,
      insertTableColumnsLeft,
      insertTableColumnsRight,
      renameTable,
      mergeTableRegion,
      unmergeTableRegion,
      mergeIntoSingleInput,
      unmergeSingleInput,
    ]
  );
  return <TableOpContext.Provider value={contextValue}>{children}</TableOpContext.Provider>;
}

export const useTableOps = (): TableOpContextInterface => TableOpContext.useContext();

/**
 * This provides all table operations for tables outside of a NoteEditor environment.
 * For example, Structured Tables Outside the Notebook via SimpleTableEditable
 */
export const SimpleTableOpProvider = ({
  cid,
  getTable,
  getComputedTables,
  updateHandler,
  onDeleteColumns,
  onReorderRowsOrColumns,
  onInsertRowsBelow,
  onInsertRowsAbove,
  children,
}: {
  cid: string;
  getTable: () => Table;
  getComputedTables: () => OutputTableMap;
  updateHandler: (
    index: number,
    {
      table,
      selection,
    }: {table: Table; selection: {selection: BenchlingSelection} | MetadataForRestore<TableIndex> | null}
  ) => void;
  onDeleteColumns?: (
    index: number,
    start: number,
    end: number,
    deleteTableColumns: (index: number, start: number, end: number) => void
  ) => void;
  onReorderRowsOrColumns?: (index: number, options: ReorderOptions) => void;
  onInsertRowsBelow?: (index: number, start: number, end: number) => void;
  onInsertRowsAbove?: (index: number, start: number, end: number) => void;
  children: React.ReactNode;
}): JSX.Element => {
  const performSimpleTableOp = useCallback(
    ({
      op,
      options,
    }: {
      op: TableOpFunction;
      options: TableOpOptions;
    }): {
      selection: {selection: BenchlingSelection} | MetadataForRestore<TableIndex> | null;
      table: Table;
    } | null => {
      // Apply the operation to retrieve the new table map, along with the desired selection.
      const oldTable = getTable();
      const computedTables = getComputedTables();
      const oldTables = {[oldTable.name]: oldTable};
      const {tables: newTables, box} = excess.ops.wrapTableOpForSingleDoc(op)(
        oldTables,
        computedTables,
        oldTable.name
      );
      const newTable = newTables[oldTable.name];

      if (TableUtil.isInvalidOpWithWarn(oldTable, newTable, options)) {
        return null;
      }

      const selection = !box
        ? null
        : {
            multiSelectKey: TableUtil.getTableMultiSelectKey(cid),
            data: {
              multiActive: false,
              multiSelIndex: [box.minI, box.minJ] satisfies TableIndex,
              multiStartIndex: [box.minI, box.minJ] satisfies TableIndex,
              multiEndIndex: [box.maxI, box.maxJ] satisfies TableIndex,
              multiActualIndex: [box.minI, box.minJ] satisfies TableIndex,
            },
          };
      return {selection, table: newTable};
    },
    [cid, getTable, getComputedTables]
  );

  const insertTableColumnsLeft = useCallback(
    (index: number, start: number, end: number, columnInfo: InsertableColumnInfo | {} = {}): void => {
      const tableOpResult = performSimpleTableOp({
        op: (table, documentName) =>
          excess.ops.insertColumnsLeft(table, documentName, start, end, columnInfo),
        options: {forColumnEdit: true},
      });
      // eslint-disable-next-line benchlint/no-eqeqeq-null
      if (tableOpResult === null) {
        return;
      }

      const {selection, table} = tableOpResult;
      updateHandler(index, {table, selection});
    },
    [performSimpleTableOp, updateHandler]
  );

  const insertTableColumnsRight = useCallback(
    (index: number, start: number, end: number, columnInfo: InsertableColumnInfo | {} = {}): void => {
      const tableOpResult = performSimpleTableOp({
        op: (table, documentName) =>
          excess.ops.insertColumnsRight(table, documentName, start, end, columnInfo),
        options: {forColumnEdit: true},
      });
      // eslint-disable-next-line benchlint/no-eqeqeq-null
      if (tableOpResult === null) {
        return;
      }
      const {selection, table} = tableOpResult;
      updateHandler(index, {table, selection});
    },
    [performSimpleTableOp, updateHandler]
  );

  const insertTableRowsAbove = useCallback(
    (index: number, start: number, end: number): void => {
      if (onInsertRowsAbove) {
        onInsertRowsAbove(index, start, end);
        return;
      }
      const tableOpResult = performSimpleTableOp({
        op: (table, documentName) => excess.ops.insertRowsAbove(table, documentName, start, end),
        options: {forColumnEdit: true},
      });
      if (!tableOpResult) {
        return;
      }
      const {selection, table} = tableOpResult;
      updateHandler(index, {table, selection});
    },
    [performSimpleTableOp, updateHandler, onInsertRowsAbove]
  );

  const insertTableRowsBelow = useCallback(
    (index: number, start: number, end: number): void => {
      if (onInsertRowsBelow) {
        onInsertRowsBelow(index, start, end);
        return;
      }
      const tableOpResult = performSimpleTableOp({
        op: (table, documentName) => excess.ops.insertRowsBelow(table, documentName, start, end),
        options: {forColumnEdit: true},
      });
      if (!tableOpResult) {
        return;
      }
      const {selection, table} = tableOpResult;
      updateHandler(index, {table, selection});
    },
    [performSimpleTableOp, updateHandler, onInsertRowsBelow]
  );

  const reorderTableRowsOrColumns = useCallback(
    (index: number, options: ReorderOptions): void => {
      if (onReorderRowsOrColumns) {
        onReorderRowsOrColumns(index, options);
        return;
      }
      const tableOpResult = performSimpleTableOp({
        op: (table, documentName) => excess.ops.reorderRowsOrColumns(table, documentName, options),
        options: {forColumnEdit: true},
      });
      if (!tableOpResult) {
        return;
      }
      const {selection, table} = tableOpResult;
      updateHandler(index, {table, selection});
    },
    [performSimpleTableOp, updateHandler, onReorderRowsOrColumns]
  );

  const deleteTableColumns = useCallback(
    (index: number, start: number, end: number): void => {
      const tableOpResult = performSimpleTableOp({
        op: (table, documentName) => excess.ops.deleteColumns(table, documentName, start, end),
        options: {forColumnEdit: true},
      });
      // eslint-disable-next-line benchlint/no-eqeqeq-null
      if (tableOpResult === null) {
        return;
      }
      const {selection, table} = tableOpResult;
      updateHandler(index, {table, selection});
    },
    [performSimpleTableOp, updateHandler]
  );

  const handleDeleteColumns = useCallback(
    (index: number, start: number, end: number): void => {
      if (onDeleteColumns) {
        onDeleteColumns(index, start, end, deleteTableColumns);
        return;
      }
      deleteTableColumns(index, start, end);
    },
    [deleteTableColumns, onDeleteColumns]
  );

  const deleteTableRowsNonContiguous = useCallback(
    (index: number, ranges: Array<[number, number]>): void => {
      if (assertAndContinue(ranges.length > 0, 'Cannot delete rows with empty ranges')) {
        const tableOpResult = performSimpleTableOp({
          op: (table, documentName) => excess.ops.deleteRowsNonContiguous(table, documentName, ranges),
          options: {forRowRemoval: true},
        });

        // eslint-disable-next-line benchlint/no-eqeqeq-null
        if (tableOpResult === null) {
          return;
        }
        const {selection, table} = tableOpResult;

        updateHandler(index, {table, selection});
      }
    },
    [performSimpleTableOp, updateHandler]
  );

  const deleteTableRows = useCallback(
    (index: number, start: number, end: number): void => {
      const tableOpResult = performSimpleTableOp({
        op: (table, documentName) => excess.ops.deleteRows(table, documentName, start, end),
        options: {forRowRemoval: true},
      });
      // eslint-disable-next-line benchlint/no-eqeqeq-null
      if (tableOpResult === null) {
        return;
      }
      const {selection, table} = tableOpResult;

      updateHandler(index, {table, selection});
    },
    [performSimpleTableOp, updateHandler]
  );

  const renameTable = useCallback(
    (index: number, newTableName: string): void => {
      const tableOpResult = performSimpleTableOp({
        op: (table, documentName, evaluatedTable, tableMap) =>
          excess.ops.renameTable(tableMap, documentName, table.name, newTableName),
        options: {},
      });
      // eslint-disable-next-line benchlint/no-eqeqeq-null
      if (tableOpResult === null) {
        return;
      }
      const {selection, table} = tableOpResult;

      updateHandler(index, {table, selection});
    },
    [performSimpleTableOp, updateHandler]
  );

  const contextValue: TableOpContextInterface = useMemo(
    () => ({
      performTableOp: noop,
      deleteTableRowsNonContiguous,
      deleteTableRows,
      deleteTableColumns: handleDeleteColumns,
      insertTableRowsAbove,
      insertTableRowsBelow,
      reorderTableRowsOrColumns,
      insertTableColumnsLeft,
      insertTableColumnsRight,
      renameTable,
      mergeTableRegion: noop,
      unmergeTableRegion: noop,
      mergeIntoSingleInput: noop,
      unmergeSingleInput: noop,
    }),
    [
      deleteTableRowsNonContiguous,
      deleteTableRows,
      handleDeleteColumns,
      insertTableRowsAbove,
      insertTableRowsBelow,
      reorderTableRowsOrColumns,
      insertTableColumnsLeft,
      insertTableColumnsRight,
      renameTable,
    ]
  );
  return <TableOpContext.Provider value={contextValue}>{children}</TableOpContext.Provider>;
};
